/**
 * API 响应处理工具
 * 统一处理后端返回的 ApiResponse 格式和直接返回数据的格式
 */

/**
 * 解析 ApiResponse 格式的响应
 * 格式：{ code: 200, message: "...", data: {...} }
 * @param {Object} response - axios 响应对象
 * @returns {Object} { success: boolean, data?: any, message?: string, code?: number }
 */
export function parseApiResponse(response) {
  if (!response || !response.data) {
    return {
      success: false,
      message: '响应数据为空'
    };
  }

  const responseData = response.data;

  // 如果已经是处理过的格式，直接返回
  if (responseData.success !== undefined) {
    return responseData;
  }

  // 处理 ApiResponse 格式
  if (responseData.code !== undefined) {
    if (responseData.code === 200) {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message,
        code: responseData.code
      };
    } else {
      return {
        success: false,
        message: responseData.message || '请求失败',
        code: responseData.code,
        error: responseData.error
      };
    }
  }

  // 如果没有 code 字段，可能是直接返回的数据
  return {
    success: true,
    data: responseData
  };
}

/**
 * 解析直接返回数据的响应（不使用 ApiResponse 包装）
 * 格式：直接返回数据对象或字符串
 * @param {Object} response - axios 响应对象
 * @returns {Object} { success: boolean, data: any }
 */
export function parseDirectResponse(response) {
  if (!response || response.data === undefined) {
    return {
      success: false,
      message: '响应数据为空'
    };
  }

  return {
    success: true,
    data: response.data
  };
}

/**
 * 统一错误处理
 * @param {Error} error - axios 错误对象
 * @returns {Object} { success: false, message: string, code?: number, error?: any }
 */
export function handleApiError(error) {
  if (error.response) {
    const { status, data } = error.response;

    // 处理 ApiResponse 格式的错误
    if (data && data.code !== undefined) {
      return {
        success: false,
        message: data.message || '请求失败',
        code: data.code,
        error: data.error,
        httpStatus: status
      };
    }

    // 处理直接返回的错误
    if (data && data.message) {
      return {
        success: false,
        message: data.message,
        httpStatus: status
      };
    }

    // 根据 HTTP 状态码返回错误信息
    let message = '请求失败';
    if (status === 400) {
      message = '请求参数错误';
    } else if (status === 401) {
      message = '未授权，请重新登录';
    } else if (status === 403) {
      message = '没有权限访问';
    } else if (status === 404) {
      message = '资源不存在';
    } else if (status === 500) {
      message = '服务器内部错误';
    } else {
      message = `请求失败 (${status})`;
    }

    return {
      success: false,
      message,
      httpStatus: status
    };
  }

  // 网络错误或其他错误
  if (error.code === 'ECONNREFUSED') {
    return {
      success: false,
      message: '连接被拒绝，可能服务未启动'
    };
  } else if (error.code === 'ENOTFOUND') {
    return {
      success: false,
      message: '无法解析服务器地址'
    };
  } else if (error.code === 'ETIMEDOUT') {
    return {
      success: false,
      message: '请求超时'
    };
  }

  return {
    success: false,
    message: error.message || '网络连接异常'
  };
}

/**
 * 字段名转换：驼峰转下划线
 * @param {Object} obj - 需要转换的对象
 * @returns {Object} 转换后的对象
 */
export function camelToSnake(obj) {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => camelToSnake(item));
  }

  if (typeof obj !== 'object') {
    return obj;
  }

  const result = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      // 将驼峰命名转换为下划线命名
      const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
      result[snakeKey] = camelToSnake(obj[key]);
    }
  }
  return result;
}

/**
 * 字段名转换：下划线转驼峰
 * @param {Object} obj - 需要转换的对象
 * @returns {Object} 转换后的对象
 */
export function snakeToCamel(obj) {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => snakeToCamel(item));
  }

  if (typeof obj !== 'object') {
    return obj;
  }

  const result = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      // 将下划线命名转换为驼峰命名
      const camelKey = key.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
      result[camelKey] = snakeToCamel(obj[key]);
    }
  }
  return result;
}
